import moment from 'moment';
import clsx from 'clsx';
import { CSSProperties } from 'react';
import {
  Cast,
  DeviceUnknown,
  Dns,
  LiveTv,
  Power,
  Router,
  SettingsInputAntenna,
  Wifi,
} from '@material-ui/icons';

export const spacing: any = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-4',
  4: 'gap-8',
};

export const DEVICE_TYPES = [
  'Evolve Set-Top Box',
  'Digital Signage Player',
  'Access Point',
  'PoE Switch',
  'Edge Router',
  'Chromecast',
  'RCU Device',
  'MediaRoom Set-Top Box',
  'MediaRoom Play Set-Top Box',
  'Cable Modem',
  'ONT ZNID',
  'Unknown',
] as const;

export const DEVICE_STATES = {
  offline: [
    'Invalid',
    'Offline',
    'OffLine',
    'Device Offline',
    'Device Not Found',
    'Unknown Device',
  ],
  online: ['PowerOn', 'Device Online', 'Online', 'StandBy'],
  unknown: ['N/A', 'Unknown'],
  warning: ['Flagged', 'default'],
};

export const renderDeviceIcon = (
  deviceType: DeviceType | string | null,
  fontSize?: 'small' | 'medium' | 'large'
) => {
  let type;
  const size = fontSize ?? 'medium';
  if (deviceType && typeof deviceType === 'object')
    type = (deviceType as DeviceType).name;
  else type = deviceType ?? 'N/A';

  switch (type) {
    case 'Evolve Set-Top Box':
    case 'MediaRoom Set-Top Box':
    case 'Digital Signage Player':
      return <LiveTv fontSize={size} />;
    case 'Cable Modem':
    case 'ONT ZNID':
      return <Router fontSize={size} />;
    case 'Access Point':
      return <Wifi fontSize={size} />;
    case 'PoE Switch':
      return <Power fontSize={size} />;
    case 'Edge Router':
      return <SettingsInputAntenna fontSize={size} />;
    case 'Chromecast':
      return <Cast fontSize={size} />;
    case 'RCU Device':
      return <Dns fontSize={size} />;
    default:
      return <DeviceUnknown fontSize={size} />;
  }
};

export const renderHeaderTag = (
  content: string,
  headerTag?: HeaderTags,
  fontSize?: StandardSizes,
  className?: string,
  style?: CSSProperties
) => {
  const sizes = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-xl',
  };
  const classNames = clsx(className ?? '', sizes[fontSize ?? 'md']);

  switch (headerTag) {
    case 'h1':
      return (
        <h1 style={style} className={classNames}>
          {content}
        </h1>
      );
    case 'h2':
      return (
        <h2 style={style} className={classNames}>
          {content}
        </h2>
      );
    case 'h3':
      return (
        <h3 style={style} className={classNames}>
          {content}
        </h3>
      );
    case 'h4':
      return (
        <h4 style={style} className={classNames}>
          {content}
        </h4>
      );
    case 'h5':
      return (
        <h5 style={style} className={classNames}>
          {content}
        </h5>
      );
    case 'h6':
      return (
        <h6 style={style} className={classNames}>
          {content}
        </h6>
      );
    default:
      return (
        <span style={style} className={classNames}>
          {content}
        </span>
      );
  }
};

export const graphingColors = [
  {
    name: 'default',
    colors: [
      '#E59400',
      '#647890',
      '#E55454',
      '#68a1b9',
      '#68b9a3',
      '#8468b9', //light
      '#ffc14d',
      '#dbe0e6',
      '#f2abab',
      '#afdace',
      '#997d49', //other
      '#418a5e',
      '#41638a',
      '#7b418a',
      '#8a415f',
    ],
  },
  {
    name: 'nature',
    colors: [
      '#755fb6',
      '#316b6e',
      '#a36a29',
      '#ef7d56',
      '#ab9438',
      '#4f6353',
      '#5582d5',
    ],
  },
  {
    name: 'nature',
    colors: [
      '#345431',
      '#316b6e',
      '#a36a29',
      '#3b352d',
      '#ab9438',
      '#4f6353',
      '#8a9493',
    ],
  },
  {
    name: 'blue',
    colors: [
      '#002636',
      '#01364d',
      '#004866',
      '#005f87',
      '#0075a6',
      '##0085bd',
      '#0095d4',
    ],
  },
];
export const xsScreen = '(max-width: 430px)';
export const smScreen = '(max-width: 599px)';
export const mdScreen = '(max-width: 959px)';
export const lgScreen = '(max-width: 1279px)';
export const timespans = [
  {
    name: 'Today',
    startDate: moment().startOf('day').toDate(),
    endDate: moment().endOf('day').toDate(),
  },
  {
    name: 'Yesterday',
    startDate: moment().subtract(1, 'days').startOf('day').toDate(),
    endDate: moment().subtract(1, 'days').endOf('day').toDate(),
  },
  {
    name: 'Week-to-Date',
    startDate: moment().startOf('week').toDate(),
    endDate: moment().endOf('day').toDate(),
  },
  {
    name: 'Last Week',
    startDate: moment().subtract(1, 'weeks').startOf('week').toDate(),
    endDate: moment().subtract(1, 'weeks').endOf('week').toDate(),
  },
  {
    name: 'Month-to-Date',
    startDate: moment().startOf('month').toDate(),
    endDate: moment().endOf('day').toDate(),
  },
  {
    name: 'Last Month',
    startDate: moment().subtract(1, 'months').startOf('month').toDate(),
    endDate: moment().subtract(1, 'months').endOf('month').toDate(),
  },
];

export const rangeUtil = (start: number, end: number, step: number = 1) => {
  let output = [];

  if (typeof end === 'undefined') {
    end = start;
    start = 0;
  }

  for (let i = start; i < end; i += step) {
    output.push(i);
  }

  return output;
};

export const isBase64String = (string: string): boolean => {
  return /base64/.test(string);
};

export const isGuid = (string: string): boolean => {
  return /^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/.test(
    string
  );
};

export const range = (start: number, end?: number, step?: number) => {
  let output = [];
  if (typeof end === 'undefined') {
    end = start;
    start = 0;
  }
  for (let i = start; i < end; i += step ?? 1) {
    output.push(i);
  }
  return output;
};

export const randomNumber = (min?: number, max?: number) => {
  return Math.floor(
    Math.random() * (Math.floor(max ?? 9999999) - Math.ceil(min ?? 0))
  );
};
